import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import type { VendorCatalogEntry } from "@/types/modelRegistry";
import { formatTokens } from "./format";

interface VendorPickerProps {
  value: string;
  onPick: (entry: VendorCatalogEntry) => void;
}

const MAX_SHOWN = 60;

/** OpenRouter's list, searchable. Picking fills the form from the vendor. */
export function VendorPicker({ value, onPick }: VendorPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [entries, setEntries] = useState<VendorCatalogEntry[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || entries) return;
    modelRegistryApi
      .vendorCatalog()
      .then(setEntries)
      .catch((error) => setFailure(registryErrorMessage(error)));
  }, [open, entries]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = entries ?? [];
    const hits = q
      ? all.filter((e) => `${e.modelName} ${e.name}`.toLowerCase().includes(q))
      : all;
    return hits.slice(0, MAX_SHOWN);
  }, [entries, query]);

  const pick = (entry: VendorCatalogEntry) => {
    onPick(entry);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 text-sm shadow-sm hover:bg-gray-50"
      >
        <span className={value ? "font-mono text-gray-900" : "text-gray-400"}>
          {value || "Search OpenRouter models"}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a vendor or model name"
            className="h-10 w-full border-b border-gray-100 px-3 text-sm outline-none placeholder:text-gray-400"
          />
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox">
            {shown.map((entry) => (
              <li key={entry.modelName}>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === entry.modelName}
                  onClick={() => pick(entry)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <Check
                    className={`size-4 shrink-0 ${value === entry.modelName ? "text-gray-900" : "text-transparent"}`}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm text-gray-900">
                      {entry.name}
                    </span>
                    <span className="truncate font-mono text-xs text-gray-500">
                      {entry.modelName}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-gray-500">
                    {entry.kind === "image"
                      ? "image"
                      : formatTokens(entry.contextWindow)}
                  </span>
                </button>
              </li>
            ))}
            {shown.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-gray-500">
                {failure ?? (entries ? "No match" : "Loading the catalog")}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
